import { supabase } from '../lib/supabase';

export interface SystemAnnouncement {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'warning' | 'error' | 'success';
  priority: number;
  is_active: boolean;
  start_date: string | null;
  end_date: string | null;
  dismissible: boolean;
  target_roles: string[] | null;
  created_at: string;
  updated_at: string;
}

/** Map DB rows that use starts_at/ends_at (legacy) to start_date/end_date. */
function normalizeAnnouncementRow(row: Record<string, unknown>): SystemAnnouncement {
  const r = row as Record<string, unknown> & {
    starts_at?: string | null;
    ends_at?: string | null;
    start_date?: string | null;
    end_date?: string | null;
    dismissible?: boolean;
  };
  const { starts_at, ends_at, ...rest } = r;
  const pr = rest.priority;
  const priority = typeof pr === 'number' && !Number.isNaN(pr) ? pr : 0;
  const dismissible = typeof r.dismissible === 'boolean' ? r.dismissible : true;
  const tr = rest.target_roles;
  const target_roles =
    tr == null ? null : Array.isArray(tr) ? (tr as string[]) : typeof tr === 'string' ? [tr] : null;
  return {
    ...(rest as Omit<SystemAnnouncement, 'start_date' | 'end_date' | 'priority' | 'dismissible' | 'target_roles'>),
    priority,
    dismissible,
    target_roles,
    start_date: (rest.start_date as string | null | undefined) ?? starts_at ?? null,
    end_date: (rest.end_date as string | null | undefined) ?? ends_at ?? null,
  };
}

/** PostgREST schema cache / Postgres: column missing from table */
function isMissingColumnError(err: { code?: string; message?: string } | null): boolean {
  return err?.code === 'PGRST204' || err?.code === '42703';
}

function errorMentionsColumn(err: { message?: string } | null, col: string): boolean {
  return String(err?.message || '').toLowerCase().includes(col.toLowerCase());
}

/**
 * Strip optional columns the remote DB may not have; retry until insert/update succeeds or nothing left to strip.
 */
function stripOptionalAnnouncementField(
  payload: Record<string, unknown>,
  err: { code?: string; message?: string } | null
): boolean {
  if (!isMissingColumnError(err)) return false;
  if (errorMentionsColumn(err, 'dismissible') && 'dismissible' in payload) {
    delete payload.dismissible;
    return true;
  }
  if (errorMentionsColumn(err, 'target_roles') && 'target_roles' in payload) {
    delete payload.target_roles;
    return true;
  }
  return false;
}

async function fetchAnnouncementsOrdered(activeOnly: boolean) {
  const base = supabase.from('system_announcements').select('*');
  const q = activeOnly ? base.eq('is_active', true) : base;
  let res = await q
    .order('priority', { ascending: false })
    .order('created_at', { ascending: false });
  if (
    res.error &&
    (res.error as { code?: string }).code === '42703' &&
    String((res.error as { message?: string }).message || '').includes('priority')
  ) {
    res = await (activeOnly
      ? supabase.from('system_announcements').select('*').eq('is_active', true)
      : supabase.from('system_announcements').select('*')
    ).order('created_at', { ascending: false });
  }
  return res;
}

export class AnnouncementService {
  /**
   * Get all announcements
   */
  static async getAllAnnouncements(): Promise<SystemAnnouncement[]> {
    try {
      const { data, error } = await fetchAnnouncementsOrdered(false);

      if (error) {
        console.error('Error fetching announcements:', error);
        throw error;
      }

      return (data || []).map((row: Record<string, unknown>) =>
        normalizeAnnouncementRow(row)
      );
    } catch (error) {
      console.error('Error in getAllAnnouncements:', error);
      throw error;
    }
  }

  /**
   * Get active announcements for current user
   */
  static async getActiveAnnouncements(userRole?: string): Promise<SystemAnnouncement[]> {
    try {
      // Do not filter by start/end columns in the query: some DBs use starts_at/ends_at
      // or lack indexes; filter in JS after normalizing row shape.
      const { data, error } = await fetchAnnouncementsOrdered(true);

      if (error) {
        console.error('Error fetching active announcements:', error);
        throw error;
      }

      if (!data) return [];

      const normalized = data.map((row: Record<string, unknown>) => normalizeAnnouncementRow(row));

      // Filter by role if target_roles is specified
      let announcements = normalized;
      if (userRole) {
        announcements = normalized.filter((announcement: SystemAnnouncement) => {
          if (!announcement.target_roles || announcement.target_roles.length === 0) {
            return true; // No role restriction
          }
          return announcement.target_roles.includes(userRole);
        });
      }

      // Filter by date range
      return announcements.filter((announcement: SystemAnnouncement) => {
        const nowDate = new Date();
        
        if (announcement.start_date) {
          const startDate = new Date(announcement.start_date);
          if (nowDate < startDate) return false;
        }
        
        if (announcement.end_date) {
          const endDate = new Date(announcement.end_date);
          if (nowDate > endDate) return false;
        }
        
        return true;
      });
    } catch (error) {
      console.error('Error in getActiveAnnouncements:', error);
      return [];
    }
  }

  /**
   * Create a new announcement
   */
  static async createAnnouncement(
    announcement: Omit<SystemAnnouncement, 'id' | 'created_at' | 'updated_at'>
  ): Promise<SystemAnnouncement> {
    try {
      const payload: Record<string, unknown> = {
        ...announcement,
        target_roles: announcement.target_roles || null,
      };

      let { data, error } = await supabase
        .from('system_announcements')
        .insert(payload)
        .select()
        .single();

      for (let i = 0; i < 4 && error; i++) {
        if (!stripOptionalAnnouncementField(payload, error)) break;
        ({ data, error } = await supabase
          .from('system_announcements')
          .insert(payload)
          .select()
          .single());
      }

      if (error) {
        console.error('Error creating announcement:', error);
        throw error;
      }

      return normalizeAnnouncementRow(data as Record<string, unknown>);
    } catch (error) {
      console.error('Error in createAnnouncement:', error);
      throw error;
    }
  }

  /**
   * Update an announcement
   */
  static async updateAnnouncement(
    id: string,
    updates: Partial<Omit<SystemAnnouncement, 'id' | 'created_at' | 'updated_at'>>
  ): Promise<SystemAnnouncement> {
    try {
      const payload: Record<string, unknown> = {
        ...updates,
        updated_at: new Date().toISOString(),
      };

      let { data, error } = await supabase
        .from('system_announcements')
        .update(payload)
        .eq('id', id)
        .select()
        .single();

      for (let i = 0; i < 4 && error; i++) {
        if (!stripOptionalAnnouncementField(payload, error)) break;
        ({ data, error } = await supabase
          .from('system_announcements')
          .update(payload)
          .eq('id', id)
          .select()
          .single());
      }

      if (error) {
        console.error('Error updating announcement:', error);
        throw error;
      }

      return normalizeAnnouncementRow(data as Record<string, unknown>);
    } catch (error) {
      console.error('Error in updateAnnouncement:', error);
      throw error;
    }
  }

  /**
   * Delete an announcement
   */
  static async deleteAnnouncement(id: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('system_announcements')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting announcement:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error in deleteAnnouncement:', error);
      throw error;
    }
  }

  /**
   * Track announcement dismissal (store in localStorage)
   */
  static dismissAnnouncement(announcementId: string): void {
    try {
      const dismissed = this.getDismissedAnnouncements();
      dismissed.add(announcementId);
      localStorage.setItem('dismissed_announcements', JSON.stringify(Array.from(dismissed)));
    } catch (error) {
      console.error('Error dismissing announcement:', error);
    }
  }

  /**
   * Get dismissed announcements from localStorage
   */
  static getDismissedAnnouncements(): Set<string> {
    try {
      const stored = localStorage.getItem('dismissed_announcements');
      if (stored) {
        return new Set(JSON.parse(stored));
      }
      return new Set();
    } catch (error) {
      console.error('Error getting dismissed announcements:', error);
      return new Set();
    }
  }

  /**
   * Clear all dismissals (for testing)
   */
  static clearDismissals(): void {
    localStorage.removeItem('dismissed_announcements');
  }
}

